import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiError, ErrorCode, ErrorTypes } from 'shared/api';
import { LoadingStatus, ShiftId } from 'shared/types';
import { RootState } from 'store';

import { acceptShifts, AcceptShiftsPayload, fetchOffer, processPeriodOffer, ProcessPeriodOfferPayload } from '../api';
import { getIds, getNextSelectAllState, getOfferType } from '../helpers';
import { Offer } from '../types';

interface SubmitState {
    status: LoadingStatus;
    error?: ErrorCode;
    warning?: ErrorCode;
}

interface CandidateOfferState {
    error?: ErrorCode;
    offer: Offer | null;
    status: LoadingStatus;
    submitState: SubmitState;
    selectedPeriodIds: string[];
    selectedShiftIds: ShiftId[];
    note: string;
}

interface LoadOfferThunkArgs {
    orderId: string;
    orderRoleId: string;
}

const initialState: CandidateOfferState = {
    offer: null,
    status: 'idle',
    selectedShiftIds: [],
    selectedPeriodIds: [],
    submitState: {
        status: 'idle'
    },
    note: ''
};

const candidateOfferSlice = createSlice({
    name: 'candidateOffer',
    initialState,
    reducers: {
        resetState: () => initialState,
        toggleShift: (state, { payload: { shiftId } }: PayloadAction<{ shiftId: ShiftId }>) => {
            const index = state.selectedShiftIds.indexOf(shiftId);

            if (index !== -1) {
                state.selectedShiftIds.splice(index, 1);

                return;
            }

            state.selectedShiftIds.push(shiftId);
        },
        togglePeriod: (state, { payload: { periodId } }: PayloadAction<{ periodId: string }>) => {
            const index = state.selectedPeriodIds.indexOf(periodId);

            if (index !== -1) {
                state.selectedPeriodIds.splice(index, 1);

                return;
            }

            state.selectedPeriodIds.push(periodId);
        },
        updateShiftsSelectAllState: (state) => {
            const { selectedShiftIds, offer } = state;
            const { shifts } = offer ?? {};

            if (!shifts) {
                return;
            }

            state.selectedShiftIds = getNextSelectAllState(shifts, (shift) => shift.id, selectedShiftIds);
        },
        updatePeriodsSelectAllState: (state) => {
            const { selectedPeriodIds, offer } = state;
            const { periods } = offer ?? {};

            if (!periods) {
                return;
            }

            state.selectedPeriodIds = getNextSelectAllState(periods, (period) => period.id, selectedPeriodIds);
        },
        setNote: (state, { payload }: PayloadAction<{ note: string }>) => {
            const { note } = payload;

            state.note = note;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(loadOfferThunk.pending, (state) => {
            state.status = 'pending';
        });

        builder.addCase(loadOfferThunk.rejected, (state, { payload }) => {
            state.error = payload;
            state.status = 'error';
        });

        builder.addCase(loadOfferThunk.fulfilled, (state, { payload: offer }) => {
            state.offer = offer;
            state.status = 'success';

            state.selectedShiftIds = [];
            state.selectedPeriodIds = [];

            // A lone item is rendered without a checkbox, so it has to come preselected.
            if (offer.shifts?.length === 1) {
                state.selectedShiftIds = [ offer.shifts[0].id ];
            } else if (offer.periods?.length === 1) {
                state.selectedPeriodIds = [ offer.periods[0].id ];
            }
        });

        builder.addCase(submitOfferThunk.pending, (state) => {
            state.submitState = {
                status: 'pending'
            };
        });

        builder.addCase(submitOfferThunk.rejected, (state, { payload: errorCode }) => {
            state.submitState = {
                status: 'error'
            };

            if (!errorCode) {
                return;
            }

            if (errorCode === ErrorTypes.DeprecatedOrderData) {
                state.submitState.warning = errorCode;

                return;
            }

            state.submitState.error = errorCode;
        });

        builder.addCase(submitOfferThunk.fulfilled, (state) => {
            state.submitState = {
                status: 'success'
            };
        });
    }
});

export const {
    resetState,
    updateShiftsSelectAllState,
    updatePeriodsSelectAllState,
    toggleShift,
    togglePeriod,
    setNote
} = candidateOfferSlice.actions;

export const loadOfferThunk = createAsyncThunk<
    Offer,
    LoadOfferThunkArgs,
    { state: RootState; rejectValue: ErrorCode }
>(
    'candidateOffer/loadOffer',
    async ({ orderId, orderRoleId }, thunkAPI) => {
        const response = await fetchOffer(orderId, orderRoleId);

        if (response instanceof ApiError) {
            return thunkAPI.rejectWithValue(response.errorCode);
        }

        return response;
    }
);

export const submitOfferThunk = createAsyncThunk<
    void,
    void,
    { state: RootState; rejectValue: ErrorCode }
>(
    'candidateOffer/submitOffer',
    async (_, thunkAPI) => {
        const { candidateOffer: candidateOfferState } = thunkAPI.getState();
        const { offer, note, selectedShiftIds, selectedPeriodIds } = candidateOfferState;

        if (!offer) {
            throw new Error('Offer cannot be empty on the submission stage');
        }

        const offerType = getOfferType(offer);

        let requestPromise;

        const commonPayload = {
            comment: note,
            orderId: offer.id,
            orderRoleId: offer.orderRoleId
        };

        switch (offerType) {
            case 'Shifts': {
                const payload: AcceptShiftsPayload = {
                    ...commonPayload,
                    offeredShiftIds: getIds(offer.shifts, (shift) => shift.id),
                    shiftIds: selectedShiftIds
                };
                requestPromise = acceptShifts(payload);
                break;
            }

            case 'Weeks': {
                const payload: ProcessPeriodOfferPayload = {
                    ...commonPayload,
                    offeredPeriodIds: getIds(offer.periods, (period) => period.id),
                    periodIds: selectedPeriodIds
                };
                requestPromise = processPeriodOffer(payload);
                break;
            }

            default:
                throw new Error('Invalid offerType');
        }

        const response = await requestPromise;

        if (response instanceof ApiError) {
            return thunkAPI.rejectWithValue(response.errorCode);
        }

        return response;
    }
);

export default candidateOfferSlice.reducer;
